import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { useMemo } from "react";

import type { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import Modal from "components/Modal";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";

const EntityPresentationComposer = dynamic(
  () => import("components/EntityPresentationComposer"),
  { ssr: false }
);

const EntityFilingComposer = dynamic(
  () => import("components/EntityFilingComposer"),
  { ssr: false }
);

const EntityNewsComposer = dynamic(
  () => import("components/EntityNewsComposer"),
  { ssr: false }
);

const EntityEventComposer = dynamic(
  () => import("components/EntityEventComposer"),
  { ssr: false }
);

const DiscussionComposeModal = dynamic(
  () => import("components/DiscussionComposeModal"),
  { ssr: false }
);

const EntityAnnoucementComposeButton = ({
  entity: entityProp,
  className,
  type = "default",
}: {
  entity?: Entity;
  className?: string;
  type?: "default" | "top-nav-button";
}) => {
  const router = useRouter();

  const { managedEntity } = useAuthentication();

  const entity = entityProp || managedEntity;

  const [showPresentationComposer, togglePresentationComposer] = useToggle();
  const [showNewsComposer, toggleNewsComposer] = useToggle();
  const [showFilingComposer, toggleFilingComposer] = useToggle();
  const [showEventComposer, toggleEventComposer] = useToggle();

  const [showDiscussionComposeModal, setShowDiscussionComposeModal] =
    useGlobalState("showDiscussionComposeModal", false);

  const dropDownMenu = useMemo(
    () => (
      <div
        className={twMerge(
          "bg-white",
          "py-[5px]",
          "shadow-[0_4px_30px_rgba(0,0,0,0.25)]",
          "rounded-lg"
        )}
      >
        <div
          className="cursor-pointer px-[10px] py-[5px] hover:bg-[rgba(36,169,167,0.3)]"
          onClick={togglePresentationComposer}
        >
          Presentation
        </div>
        <div
          className="cursor-pointer px-[10px] py-[5px] hover:bg-[rgba(36,169,167,0.3)]"
          onClick={() =>
            setShowDiscussionComposeModal(!showDiscussionComposeModal)
          }
        >
          Discussion
        </div>
        <div
          className="cursor-pointer px-[10px] py-[5px] hover:bg-[rgba(36,169,167,0.3)]"
          onClick={toggleNewsComposer}
        >
          News
        </div>
        <div
          className="cursor-pointer px-[10px] py-[5px] hover:bg-[rgba(36,169,167,0.3)]"
          onClick={toggleFilingComposer}
        >
          Filing
        </div>
        <div
          className="cursor-pointer px-[10px] py-[5px] hover:bg-[rgba(36,169,167,0.3)]"
          onClick={toggleEventComposer}
        >
          Event
        </div>
      </div>
    ),
    [
      setShowDiscussionComposeModal,
      showDiscussionComposeModal,
      toggleEventComposer,
      toggleFilingComposer,
      toggleNewsComposer,
      togglePresentationComposer,
    ]
  );

  const dropDownButton = useMemo(() => {
    if (type === "top-nav-button") {
      return (
        <button
          className={twMerge(
            "bg-white border-none rounded-full text-[13px] hover:font-medium hover:text-active-black",
            "mx-[12.5px] p-0",
            "group",
            "relative",
            className
          )}
        >
          <span className={twMerge("pl-[16px]", "py-[6px]")}>Post Update</span>
          <div className={twMerge("inline-block ml-[10px] pr-[8px] py-[5px]")}>
            <FontAwesomeIcon
              icon={faCaretDown}
              className="inline-block !text-active-black !text-[13px] !min-w-0"
            />
            <div
              className={twMerge(
                "absolute hidden group-hover:block",
                "right-0 w-[115px]",
                "pt-[10px]"
              )}
            >
              {dropDownMenu}
            </div>
          </div>
        </button>
      );
    }

    return (
      <button
        className={twMerge(
          "bg-white border-solid border-smart-teal rounded-full",
          "text-[13px] md:text-[15px] hover:font-medium hover:text-active-black",
          "mx-[12.5px] p-0",
          "group",
          className
        )}
      >
        <span className={twMerge("pl-[24px]", "py-[6px]", "text-smart-teal")}>
          Post Update
        </span>
        <div
          className={twMerge(
            "inline-block relative ml-[5px] pr-[16px] py-[6px]"
          )}
        >
          <FontAwesomeIcon
            icon={faCaretDown}
            className="inline-block !text-smart-teal !text-[13px] !min-w-0"
          />

          <div
            className={twMerge(
              "absolute hidden group-hover:block",
              "left-[-65px] w-[115px]",
              "pt-[10px]",
              "z-50"
            )}
          >
            {dropDownMenu}
          </div>
        </div>
      </button>
    );
  }, [className, dropDownMenu, type]);

  return (
    <>
      {dropDownButton}
      {showPresentationComposer && (
        <Modal onClose={togglePresentationComposer}>
          <EntityPresentationComposer
            entity={entity}
            onSuccess={() => {
              togglePresentationComposer();
              if (
                router.pathname === "/entities/[slug]/announcements" &&
                router.query.tab === "presentations"
              ) {
                router.reload();
              } else {
                router.push(
                  `/entities/${entity.slug}/announcements?tab=presentations`
                );
              }
            }}
            onCancel={togglePresentationComposer}
          />
        </Modal>
      )}
      {showNewsComposer && (
        <Modal onClose={toggleNewsComposer}>
          <EntityNewsComposer
            entity={entity}
            onSuccess={() => {
              toggleNewsComposer();
              if (router.pathname === "/entities/[slug]/news") {
                router.reload();
              } else {
                router.push(`/entities/${entity.slug}/news`);
              }
            }}
            onCancel={toggleNewsComposer}
          />
        </Modal>
      )}
      {showFilingComposer && (
        <Modal onClose={toggleFilingComposer}>
          <EntityFilingComposer
            entity={entity}
            onSuccess={() => {
              toggleFilingComposer();
              if (
                router.pathname === "/entities/[slug]/announcements" &&
                !router.query.tab
              ) {
                router.reload();
              } else {
                router.push(`/entities/${entity.slug}/announcements`);
              }
            }}
            onCancel={toggleFilingComposer}
          />
        </Modal>
      )}
      {showEventComposer && (
        <Modal onClose={toggleEventComposer}>
          <EntityEventComposer
            entity={entity}
            onSuccess={() => {
              toggleEventComposer();
              if (
                router.pathname === "/entities/[slug]/announcements" &&
                router.query.tab === "events"
              ) {
                router.reload();
              } else {
                router.push(
                  `/entities/${entity.slug}/announcements?tab=events`
                );
              }
            }}
            onCancel={toggleEventComposer}
          />
        </Modal>
      )}
      {showDiscussionComposeModal && (
        <DiscussionComposeModal
          commentable={entity}
          style="v3"
          onClose={() => setShowDiscussionComposeModal(false)}
          onDiscussionPost={() => {
            setShowDiscussionComposeModal(false);
            if (router.pathname === "/entities/[slug]/discussions") {
              router.reload();
            } else {
              router.push(`/entities/${entity.slug}/discussions`);
            }
          }}
        />
      )}
    </>
  );
};

export default EntityAnnoucementComposeButton;
